import React from 'react';
import styled from 'styled-components';
import { LocalizedLink, useLocalization } from 'gatsby-theme-i18n';

import {
  ContentfulComponentBackground,
  ContentfulPageCustomerStory,
  ContentfulEntry,
} from '@/../graphql-types';
import BackgroundWrapper from '@/components/BackgroundWrapper'; //helper for background css on other components
import {
  FooterCta,
  Undefined,
  StatsCloud,
  PlatformFeatureCards,
  Testimonial,
  ResourceCardSet,
  PlatformFeatureLarge,
  PlatformFeatureSet,
  PlatformFeatureStats,
  SolutionsCardCarousel,
} from '@/components/contentful/components';
import RichBody from '../components/RichBody';
import { getFullLinkByContent } from '@/utils/helpers';
import Typography from '@/components/layout/Typography';
import * as Constants from '@/types/Constants';

type BackgroundComponent = ContentfulEntry & {
  background: ContentfulComponentBackground;
};

const Container = styled.div``;

interface PageCustomerStoryProps {
  data?: ContentfulPageCustomerStory;
}
const PageCustomerStory: React.FC<PageCustomerStoryProps> = (props) => {
  const { data } = props;
  const { locale } = useLocalization();

  // if the component has a background, then wrap it with a background
  const wrapBackground = (component, element) => {
    const hasBackground = component?.['background'] != null;
    if (hasBackground) {
      return (
        <BackgroundWrapper
          key={`background-component-${component.id}`}
          data={(component as BackgroundComponent)?.background}
        >
          {element}
        </BackgroundWrapper>
      );
    } else {
      return element;
    }
  };

  return (
    <Container>
      <div>
        <RichBody
          title={data.customerStory.title}
          titlePrefix=""
          gated={false}
          content={data.customerStory.longDescription}
          download={null}
        />
        {data?.featuredProducts && (
          <div className="w-full hidden md:block md:w-1/4 flex-shrink-0 mt-md">
            <Typography variant="h4">Featured Items</Typography>
            <ul>
              {data.featuredProducts.map((product) => {
                const link = product?.compose__page?.[0]?.slug
                  ? getFullLinkByContent(
                      product?.compose__page?.[0]?.slug,
                      'ContentfulPageProduct',
                      locale,
                    )
                  : '';
                return (
                  <li
                    key={`featured-product-${product.contentful_id}`}
                    className="py-xs pl-xs text-primaryMain"
                  >
                    {link && (
                      <LocalizedLink language={locale} to={link}>
                        {product.topic.title}
                      </LocalizedLink>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
      {data.sections?.map((contentfulComponent: ContentfulEntry) => {
        // determine which contentful component should be used
        let element;
        const key = `component-${contentfulComponent.id}`;

        switch (contentfulComponent?.internal?.type) {
          case Constants.COMPONENT_PLATFORM_FEATURE_CARDS:
            element = (
              <PlatformFeatureCards key={key} data={contentfulComponent} />
            );
            break;
          case Constants.COMPONENT_PLATFORM_FEATURE_LARGE:
            element = (
              <PlatformFeatureLarge key={key} data={contentfulComponent} />
            );
            break;
          case Constants.COMPONENT_PLATFORM_FEATURE_SET:
            element = (
              <PlatformFeatureSet key={key} data={contentfulComponent} />
            );
            break;
          case Constants.COMPONENT_PLATFORM_FEATURE_STATS:
            element = (
              <PlatformFeatureStats key={key} data={contentfulComponent} />
            );
            break;
          case Constants.COMPONENT_RESOURCE_CARD_SET:
            element = <ResourceCardSet key={key} data={contentfulComponent} />;
            break;
          case Constants.COMPONENT_SOLUTIONS_CAROUSEL:
            element = (
              <SolutionsCardCarousel key={key} data={contentfulComponent} />
            );
            break;
          case Constants.COMPONENT_STATS_CLOUD:
            element = <StatsCloud key={key} data={contentfulComponent} />;
            break;
          case Constants.COMPONENT_TESTIMONIAL:
            element = <Testimonial key={key} data={contentfulComponent} />;
            break;
          default:
            element = (
              <Undefined
                key={key}
                message={`ComponentMapper: "${contentfulComponent?.internal?.type}" not defined.`}
              />
            );
        }
        return wrapBackground(contentfulComponent, element);
      })}

      {data?.footerCta && <FooterCta data={data.footerCta} />}
    </Container>
  );
};
export default PageCustomerStory;
